import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { AlertTriangle, Clock, Lock } from 'lucide-react';

/**
 * PilotSnapshotBanner
 *
 * Reads `GET /api/pilot/status` (public — survives the pilot kill switch)
 * and renders a compact warning strip in the topbar when the pilot is
 * approaching its hard stop (≤ 14 days) or expired.
 *
 * Behaviour:
 *   • tier=ok        → renders nothing.
 *   • tier=warning   → soft amber strip ("X days remaining").
 *   • tier=critical  → red strip ("X days — extend or wind down").
 *   • tier=expired   → full-page modal lock; nothing dismissible.
 *
 * Polls every 5 minutes so a mid-pilot kill-switch flip propagates to
 * connected sessions within a reasonable window. Cheaper than websockets,
 * good enough for an internal-only Pilot tool.
 */
const POLL_MS = 5 * 60 * 1000;

const PilotSnapshotBanner = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await axiosInstance.get('/pilot/status');
        if (!cancelled) setStatus(data);
      } catch {
        // Silent — banner is best-effort, never blocks the app.
      }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!status || !status.configured || status.banner_tier === 'ok') return null;

  // Owner rescue hatch — never block the login page or the Owner
  // Controls screen: the owner unlocks the system from there.
  const path = window.location.pathname || '';
  if (path.startsWith('/owner-controls') || path.startsWith('/login')) return null;

  if (status.banner_tier === 'expired') {
    return (
      <div
        data-testid="pilot-expired-overlay"
        className="fixed inset-0 z-[3000] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm"
      >
        <div className="max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            انتهت مدة النسخة التجريبية
          </h2>
          <p className="text-sm text-gray-600 mb-2">
            Pilot Snapshot Expired
          </p>
          <p className="text-sm text-gray-700 leading-relaxed mb-4">
            النظام محجوب مؤقتاً. للتمديد أو الإفراج، تواصل مع مالك النظام.
          </p>
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
            <div>تاريخ البدء: <span className="font-mono text-gray-700">{status.start_date}</span></div>
            <div>تاريخ الانتهاء: <span className="font-mono text-gray-700">{status.expires_at}</span></div>
            <div>المدة: <span className="font-mono text-gray-700">{status.duration_days} يوم</span></div>
          </div>
          <a
            href="/owner-controls"
            data-testid="pilot-owner-rescue-link"
            className="inline-block mt-4 text-xs text-indigo-600 hover:text-indigo-800 underline"
          >
            أنا المالك — فتح شاشة التحكم
          </a>
        </div>
      </div>
    );
  }

  const isCritical = status.banner_tier === 'critical';
  const cls = isCritical
    ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-amber-50 border-amber-200 text-amber-800';

  return (
    <div
      data-testid="pilot-snapshot-banner"
      data-tier={status.banner_tier}
      className={`border-b px-4 py-2 text-sm flex items-center justify-center gap-2 ${cls}`}
    >
      {isCritical ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
      <span className="font-semibold" style={{ direction: 'ltr' }}>
        Pilot Snapshot · {status.days_remaining} day{status.days_remaining === 1 ? '' : 's'} remaining
      </span>
      <span className="opacity-60">·</span>
      <span>النسخة التجريبية تنتهي في <span className="font-mono">{status.expires_at}</span></span>
    </div>
  );
};

export default PilotSnapshotBanner;
